// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## EventHandler.js
//
// Handles events dispatched from an Editor() to modify music() objects.
/* tslint:disable object-shorthand-properties-first */
import type { Accidental, NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import Note from './note';
import type NoteHolder from './noteHolder';
import type Editable from './editable';
import Rhythm from './rhythm';

interface FocusInOptions {
  duplicatePriorNote: boolean;
  accidental: 'bb' | 'b' | '' | '#' | 'x' | 'prior' | 'key';
}

type EditableEventTypes =
  | 'contactEventStart'
  | 'pitchChange'
  | 'accidentalChange'
  | 'select'
  | 'unselect'
  | 'delete';

type EditableParentEventTypes = `note${Capitalize<EditableEventTypes>}`;

type EventHandlerListener = (...rest: any[]) => void;
type EventHandlerParentListener = (type: EditableParentEventTypes, ...rest: any[]) => void;

const debug = false;
const log = debug
  ? (...args: any[]) => {
      console.log(...args);
    }
  : () => {};

interface Coordinates {
  x: number;
  y: number;
}

export default class EventHandler {
  noteHolder: NoteHolder;

  editable: Editable;

  listeners: {
    type: EditableEventTypes;
    callback: EventHandlerListener;
  }[] = [];

  parentListeners: EventHandlerParentListener[] = [];

  private _accidental: {
    locked: boolean;
    value: Accidental;
  } = {
    locked: false,
    value: '',
  };

  private _poppedNote: false | { note: Note; dragMode: boolean } = false;

  private recentEventState?: Coordinates;

  get accidental(): Accidental {
    return this._accidental.value;
  }

  set accidental(value: Accidental) {
    if (this._accidental.locked) return;
    this._accidental.value = value;
  }

  get hasPoppedSelected() {
    return !!(this._poppedNote && this._poppedNote.dragMode);
  }

  public lockAccidental(value: Accidental) {
    this._accidental.locked = true;
    this._accidental.value = value;
  }

  public unlockAccidental() {
    this._accidental.locked = false;
  }

  // Public constructor
  constructor(noteHolder: NoteHolder, editable: Editable) {
    this.noteHolder = noteHolder;
    this.editable = editable;
    log('eventHandler', this);
    log('for editable', this.editable);
  }

  // Keyboard handlers:

  private getPrior() {
    if (!this.editable.editor) {
      throw Error('Editable.editor must be set before EventHandler.getPrior can be called.');
    }
    const thisIndex = this.editable.editor.editables.indexOf(this.editable);
    const priorEditable = this.editable.editor.editables[thisIndex - 1];
    if (!priorEditable) return false;
    return priorEditable;
  }

  /**
   * Select the first selectable note, if possible. By default we add a note on
   * the third line of the staff when we keyboard in to an empty editable.
   * Returns whether a redraw is needed
   */
  public keyboardFocusIn(options: Partial<FocusInOptions> = {}) {
    if (!this.editable.clef || !this.editable.stave) {
      throw Error(
        'Editable.clef & Editable.stave must be set before EventHandler.keyboardFocusIn can be called.'
      );
    }

    const defaultOptions: FocusInOptions = { duplicatePriorNote: true, accidental: '' };
    options = { ...defaultOptions, ...options };

    log('keyboardFocusIn');
    // if something is already selected, abort.
    if (this.editable.selectedNotes.length) return false;

    // If we have note(s) and they're selectable, try selecting the lowest one:
    if (this.editable.options.selectable && !this.noteHolder.isNeitherNoteNorRest()) {
      const box = this.editable.getBoundingBox();
      const middleX = box.x + box.w / 2;
      const bottomY = box.y + box.h;
      const selected = this.selectIfPossible({ x: middleX, y: bottomY });
      if (selected) return true;
    }

    // Otherwise, let's try adding a note as close to the desired line as possible
    let desiredLine = 3; // middle of the staff, per normal line counting (not VexFlow)
    if (options.duplicatePriorNote) {
      const prior = this.getPrior();
      if (prior && prior.noteHolder.length) {
        const note = prior.selectedNotes[0] || prior.noteHolder.notes[0];
        if (note) desiredLine = this.editable.clef.getLine(note);
      }
    }

    let possibleLines = [];
    const highestLine = this.editable.options.highestLine || 7;
    const lowestLine = this.editable.options.lowestLine || -1;
    if (lowestLine > highestLine)
      throw new Error('lowestLine must be a smaller number than highest line');
    for (let i = lowestLine; i <= highestLine; i += 0.5) {
      possibleLines.push(i);
    }

    possibleLines = possibleLines
      .filter(
        (line) =>
          !this.editable.options.fixedNotes.find(
            (note) => this.editable.clef!.getLine(note) === line
          )
      )
      .sort((a, b) => Math.abs(desiredLine - a) - Math.abs(desiredLine - b));

    log('possibleLines', possibleLines);
    if (!possibleLines.length) return false;
    const vexLine = 5 - possibleLines[0]!;

    // If we don't have note(s), add & select a note:
    const y = this.editable.stave.getYForLine(vexLine);
    return this.addIfPossible({ x: 0, y }, { select: true }); // x doesn't matter, really...
  }

  /**
   * If the changing note can be moved to the linked editable, do it.
   */
  private giveToLinkedEditableIfPossible(newNote: Note, noteToMove: Note) {
    log('Has linked editable?', !!this.editable.linkedEditable && !!this.editable.linkedEditable);
    if (!this.editable.linkedEditable) return false;

    log(
      "Which isn't full?",
      !!this.editable.linkedEditable && !this.editable.linkedEditable.isFull(1)
    );
    log(
      'And for which new note is in range?',
      !!this.editable.linkedEditable && this.editable.linkedEditable.noteIsInStaffRange(newNote)
    );

    // If the note would be out of bounds, let's try and give it to our
    // linked editable:
    if (
      !this.editable.linkedEditable.isFull(1) &&
      this.editable.linkedEditable.noteIsInStaffRange(newNote)
    ) {
      log('giving to linked editable');
      // Now remove the note we were moving from this staff. We have to do this
      // first because there might not be room for the note on the other staff
      // otherwise.
      if (this._poppedNote) {
        this._poppedNote = false;
      } else {
        this.noteHolder.remove(noteToMove);
      }

      const linkedEditable = this.editable.linkedEditable;
      const linkedHandler = linkedEditable.eventHandler;
      // First pend it as a note where it's going:
      linkedHandler._poppedNote = {
        note: newNote,
        dragMode: true,
      };
      // Save it as a state there, too:
      const linkedYState = linkedHandler.getYAtNote(noteToMove);
      linkedHandler.saveState({ x: 0, y: linkedYState });
      // Select it:
      linkedEditable.selected = true;
      this.editable.selected = false;
      // And put it there!
      const linkedYNew = linkedHandler.getYAtNote(newNote);
      linkedHandler.changeNote({ x: 0, y: linkedYNew }, true);
      return true;
    }
    log('could not give it to linked editable');
    return false;
  }

  /**
   * Move selected note up or down; add a new note if nothing selected
   * @param {'up' | 'down'} direction
   * @returns {boolean} redraw needed
   */
  public moveUpOrDown(direction: 'up' | 'down') {
    log('moveUpOrDown', direction);
    const lineChange = direction === 'up' ? -0.5 : 0.5;

    // If there's a pending note, change it up or down (don't delete -- this code
    // can happen if moused over, then arrow keys start to be used, we'll want to
    // move the pending note, rather than any selected note, and commit it.)
    if (this.editable.pendingNote) {
      const line = this.getVexLineAtNote(this.editable.pendingNote) + lineChange;
      const y = this.getYAtVexLine(line);
      return this.changeNote({ x: 0, y }, true);
    }

    // If there's a selected note or a popped note, change it up or down
    if (this.editable.selectedNotes.length || this._poppedNote) {
      const noteToMove =
        this.editable.selectedNotes[0] || (this._poppedNote && this._poppedNote.note);
      if (!noteToMove) return false; // this should never happen;
      const line = this.getVexLineAtNote(noteToMove) + lineChange;
      const y = this.getYAtVexLine(line);
      log('y', y);
      log('line', line);

      // If it's out of range, let's try to give it to our linked editable:
      const newNote = noteToMove.getNoteAtGeneric(direction === 'up' ? 2 : -2);
      if (!this.editable.noteIsInStaffRange(newNote)) {
        if (this.giveToLinkedEditableIfPossible(newNote, noteToMove)) {
          log('gave it to linked editable at moveUpOrDown');
          return true;
        }
        log('could not give it to linked editable at moveUpOrDown');
      }

      const redrawNeeded = this.changeNote({ x: 0, y }, true);
      if (this._poppedNote) {
        // Because we'll never get a mouse at new coords, we have to update the
        // popped note to occupy the space of the existing note
        this._poppedNote.note = this._poppedNote.note.getNoteAtGeneric(direction === 'up' ? 2 : -2);
        this.saveState({ x: 0, y: this.getYAtNote(this._poppedNote.note) });
      }
      return redrawNeeded;
    }

    // If there's no note, call keyboardFocusIn to try and create one
    return this.keyboardFocusIn();
  }

  /**
   * Cycles selection focus within a noteHolder
   * @param direction - though the user's actual keys are shift+left shift+right
   */
  public selectPriorNextTopOrBottomNote(direction: 'prior' | 'next' | 'top' | 'bottom') {
    if (!this.editable.editor) {
      throw Error(
        'Editable.editor must be set before EventHandler.selectPriorNextTopOrBottomNote can be called.'
      );
    }

    log('selectPriorOrNextNoteInEditable', direction);
    log('select prior or next');
    if (!this.editable.options.selectable) return false;

    // Filter notes by whether they are selectable:
    const notes = this.noteHolder.notes
      .filter((nonFixedNote) => !this.isFixedNote(nonFixedNote))
      .sort(
        (a, b) =>
          a.clone().setAccidental('').getMidiNumber() - b.clone().setAccidental('').getMidiNumber()
      );
    if (!notes.length) return false;

    // Find the index of the currently selected one
    const currentlySelectedIndex = notes.findIndex(
      (note) => !!this.editable.selectedNotes.find((sel) => sel.equals(note))
    );
    let toBeSelectedIndex = currentlySelectedIndex + (direction === 'prior' ? -1 : 1);
    log('toBeSelectedIndex', toBeSelectedIndex);
    // But if we're selecting top or bottom, it doesn't really matter:
    if (direction === 'top') {
      log('selecting top');
      toBeSelectedIndex = notes.length - 1;
    } else if (direction === 'bottom') {
      log('selecting bottom');
      toBeSelectedIndex = 0;
    } else if (toBeSelectedIndex >= notes.length || toBeSelectedIndex < 0) {
      // do we have a linked editable that should select a note instead?
      if (this.editable.linkedEditable) {
        // can we select on it
        const itSelectedSomething =
          this.editable.linkedEditable.eventHandler.selectPriorNextTopOrBottomNote(
            direction === 'next' ? 'bottom' : 'top'
          );
        // if not, select the 0th note
        if (itSelectedSomething) {
          this.editable.selected = false;
          this.editable.selectedNotes = [];
          return true;
        }
      }
      // Otherwise, cycle around, by being sure it's a positive number in range:
      toBeSelectedIndex += notes.length;
      toBeSelectedIndex %= notes.length;
    }

    // Increment that index (which might be -1) by 1 to select a new note
    const selectedNote = notes[toBeSelectedIndex % notes.length]!;
    this.editable.editor.deselectAll(true);
    this.editable.selectedNotes = [selectedNote];
    this.editable.selected = true;
    this.triggerListeners('select');
    return true;
  }

  /**
   * Add a note a step above or below the selected one if possible; returns
   * true if redraw needed.
   */
  public addUpOrDown(direction: 'up' | 'down'): boolean {
    log('addUpOrDown', direction);
    if (!this.editable.clef || !this.editable.stave) {
      throw Error(
        'Editable.clef & Editable.stave must be set before EventHandler.addUpOrDown can be called.'
      );
    }

    // If there is no selected note, create & select a new one
    if (
      this.editable.selectedNotes.length === 0 &&
      !(this.editable.noteHolder.rest && this.editable.selected)
    ) {
      return !!this.keyboardFocusIn();
    }

    // We know there is a selected note because of above
    const selected = this.editable.selectedNotes[0]!;
    // If the new note would be out of bounds, try adding it to our linked editable
    const newNote = selected.getNoteAtGeneric(direction === 'up' ? 2 : -2);
    if (!this.editable.noteIsInStaffRange(newNote)) {
      // Pop it here:
      this._poppedNote = {
        note: selected.clone().setAccidental(''),
        dragMode: true,
      };
      // Now try to give it there:
      if (this.giveToLinkedEditableIfPossible(newNote, selected)) {
        return true;
      }
      this._poppedNote = false;
      return false;
    }

    const line = this.editable.clef.getVexLine(selected);
    const offset = direction === 'up' ? -0.5 : 0.5;
    const y = this.editable.stave.getYForLine(line + offset);
    // We'll add if possible, select otherwise:
    return this.contactEventStart({ x: 0, y });
  }

  public isFixedNote(note: Note | NoteName): boolean {
    return !!this.editable.options.fixedNotes.find((fixed) => fixed.equals(note));
  }

  /** Get the staff line at a Y value (as a standard staff line measure) */
  public getStaffLineAtY(y: number): number {
    // log('getLineAtY', arguments);
    if (!this.editable.stave) {
      throw Error('Editable.stave must be set before EventHandler.getStaffLineAtY can be called.');
    }

    const line5 = this.editable.stave.getYForLine(0);
    // get spacing between lines
    const spacing = this.editable.stave.getSpacingBetweenLines();
    // calculate the distance from line5 to mouse.y:
    //  a positive number means we're above line5.
    const distance = line5 - y;
    const unroundedLinesDifferent = distance / spacing;
    // Round it to 0.5 accuracy:
    const linesDifferent = Math.round(2 * unroundedLinesDifferent) / 2;
    const line = 5 + Number(linesDifferent);
    //      log("y: " + y + " line: " + line);
    return line;
  }

  /** Get the Y value at the vex stave line (top line of staff = line 0 in vex) */
  private getYAtVexLine(vexLine: number) {
    if (!this.editable.stave) {
      throw Error('Editable.stave must be set before EventHandler.getYAtVexLine can be called.');
    }
    return this.editable.stave.getYForLine(vexLine);
  }

  private getVexLineAtNote(note: Note) {
    if (!this.editable.clef) {
      throw Error('Editable.clef must be set before EventHandler.getVexLineAtNote can be called.');
    }
    return this.editable.clef.getVexLine(note);
  }

  private getYAtNote(note: Note) {
    return this.getYAtVexLine(this.getVexLineAtNote(note));
  }

  private getNoteAtY(y: number): Note {
    // log('getNoteAtY', arguments);
    if (!this.editable.clef) {
      throw Error('Editable.clef must be set before EventHandler.getNoteAtY can be called.');
    }

    const note = this.editable.clef.getNoteAtLine(this.getStaffLineAtY(y));
    // GCR: This seems like it shouldn't be here...
    this.setNoteAccidental(note);
    return note;
  }

  private setNoteAccidental(note: Note) {
    if (this.accidental) {
      log('setting accidental to this.accidental', this.accidental);
      note.accidental = this.accidental;
      return note;
    }
    if (this.editable.options.keySignature) {
      log(
        'setting accidental to keySig.getAccidentalAtNote',
        this.editable.options.keySignature.relativeMajor.getAccidentalAtNote(note)
      );
      note.accidental = this.editable.options.keySignature.relativeMajor.getAccidentalAtNote(note);
    }
    return note;
  }

  private noteExistsAtY(y: number) {
    log('noteExistsAtY', y);
    const note = this.getNoteAtY(y);
    return this.noteHolder.noteExistsAtIgnoreAccidental(note);
  }

  /**
   * Add a note or a rest if possible, either pending or committed
   * depending on whether we're responding to a hover or click event, and whether the editable
   * is in 'rest' or 'note' mode. Returns true if something was added & a re-render is needed.
   * @param coords
   * @param options
   * @param {boolean} options.pend should the note/rest we add be pending?
   * @param {boolean} options.select should we select the note/rest we add?
   * @returns {boolean | Note} whether a change occurred and a re-render is needed, or the Note
   * that was added.
   */
  private addIfPossible(
    coords: Coordinates,
    options: { pend?: boolean; select?: boolean } = { pend: false, select: false }
  ): boolean | Note {
    if (this.editable.pendingMode === 'note') return this.addNoteIfPossible(coords, options);
    return this.addRestIfPossible(options);
  }

  /**
   * Add a note at the specified coordinates or a rest if possible, either pending or committed
   * depending on whether we're responding to a hover or click event, and whether the editable
   * is in 'rest' or 'note' mode. Returns true if something was added & a re-render is needed.
   * @param coords
   * @param options
   * @param {boolean} options.pend should the note/rest we add be pending?
   * @param {boolean} options.select should we select the note/rest we add?
   * @returns {boolean | Note} whether a change occurred and a re-render is needed, or the Note
   * that was added.
   */
  private addNoteIfPossible(
    coords: Coordinates,
    options: { pend?: boolean; select?: boolean } = { pend: false, select: false }
  ): false | Note {
    // If we're in rest mode and have gotten here... well, this should never happen
    if (this.editable.pendingMode !== 'note') {
      throw new Error('addNoteIfPossible can only be called when editable.pendingMode === "note"');
    }
    if (!this.editable.clef) {
      throw Error('Editable.clef must be set before EventHandler.addNoteIfPossible can be called.');
    }
    if (!this.editable.editor) {
      throw Error(
        'Editable.editor must be set before EventHandler.addNoteIfPossible can be called.'
      );
    }

    const select = !!options.select;
    const pend = options.pend;

    log('addNoteIfPossible', coords, options);

    // If we're full or pitch isn't editable, we can't add a note:
    if (this.editable.isFull()) return false;

    // What note are we trying to add?
    const noteToAdd = this.editable.options.lockedPitch
      ? this.editable.options.lockedPitch.clone()
      : this.getNoteAtY(coords.y);

    this.setNoteAccidental(noteToAdd);

    // If the note's out of bounds, Abort!
    const line = this.editable.clef.getLine(noteToAdd);
    if (line > this.editable.options.highestLine || line < this.editable.options.lowestLine) {
      return false;
    }

    // Is there a note at our event location? Abort!
    const noteAlreadyExists = this.noteHolder.noteExistsAtIgnoreAccidental(noteToAdd);
    if (noteAlreadyExists) return false;

    // Are we in pending mode, and there's already a pending note at our event location? Abort!
    const pendingNoteAlreadyExists =
      pend &&
      this.editable.pendingNote &&
      this.editable.pendingNote.equalsIgnoreAccidental(noteToAdd);
    if (pendingNoteAlreadyExists) return false;

    // We now know these things need to be set, because it's no kind of rest and
    // and we will be adding a note if it's in range... or we're going out of range:
    this.editable.noteHolder.rest = false;
    this.editable.pendingRest = false;
    this.editable.placeHolderRest = false;
    this._poppedNote = false;

    // Add a pending note if indicated:
    if (pend) {
      log('adding pending note');
      this.editable.pendingNote = noteToAdd;
      this.saveState(coords);
      return noteToAdd;
    }

    // Add a note, and mark as selected as indicated:
    this.editable.pendingNote = false;
    this.noteHolder.push(noteToAdd);
    this.triggerListeners('pitchChange', noteToAdd);
    if (select) {
      this.editable.editor.deselectAll(true);
      this.editable.selectedNotes = [noteToAdd];
      this.editable.selected = true;
    }
    this.saveState(coords);
    return noteToAdd;
  }

  /**
   * Add a rest if possible, either pending or committed
   * depending on whether we're responding to a hover or click event.
   * Returns true if something was changed & a re-render is needed.
   * @param options
   * @param {boolean} options.pend should the note/rest we add be pending?
   * @param {boolean} options.select should we select the note/rest we add?
   * @returns {boolean} whether a change occurred and a re-render is needed, or the Note
   * that was added.
   */
  private addRestIfPossible(
    options: { pend?: boolean; select?: boolean } = { pend: false, select: false }
  ) {
    if (this.editable.pendingMode !== 'rest') {
      throw new Error('addRestIfPossible can only be called when editable.pendingMode === "rest"');
    }

    const hoverEvent = !!options.pend;
    const clickEvent = !options.pend;
    const select = !!options.select;

    // If we're hovering, and there are no notes or rests present, and it's not already
    // a pending rest, then make it a pending rest and render.
    if (
      hoverEvent &&
      this.editable.noteHolder.isNeitherNoteNorRest() &&
      !this.editable.pendingRest
    ) {
      this._poppedNote = false;
      this.editable.pendingNote = false;
      this.editable.pendingRest = true;
      this.editable.placeHolderRest = false;
      return true;
    }

    // If we've clicked in rest mode, and there are no notes or rests present, make it a committed
    // rest
    if (clickEvent && this.editable.noteHolder.isNeitherNoteNorRest()) {
      this.editable.pendingNote = false;
      this._poppedNote = false;
      this.editable.pendingRest = false;
      this.editable.placeHolderRest = false;
      this.editable.noteHolder.rest = true;
      this.editable.selected = select;
      return true;
    }

    return false;
  }

  private saveState(coords: Coordinates) {
    log('saveState', coords);
    // store the coordinates in recentEventState & recentObjectState:
    this.recentEventState = coords;
  }

  /** Selects a note if possible; returns boolean redrawNeeded */
  private selectIfPossible(coords: Coordinates) {
    log('selectIfPossible', coords);
    if (!this.editable.editor) {
      throw Error(
        'Editable.editor must be set before EventHandler.selectIfPossible can be called.'
      );
    }

    // If a note exists there & we're ghosting,
    // we've got to git rid of the ghosting note:
    const note = this.getNoteAtY(coords.y);
    if (this.noteHolder.noteExistsAtIgnoreAccidental(note)) {
      this._poppedNote = false;
      this.editable.pendingNote = false;
    }

    // set selected if its a rest:
    if (this.noteHolder.length === 0 && this.noteHolder.rest) {
      log('selecting rest');
      this.editable.selected = true;
      this.triggerListeners('select');
      return true;
    }

    let redrawNeeded = false;
    const selectableNote = this.noteHolder.notes.find(
      (nonFixedNote) => !this.isFixedNote(nonFixedNote)
    );
    if (selectableNote && this.editable.options.selectable && !this.editable.selected) {
      this.editable.selected = true;
      redrawNeeded = true;
    }

    const nearestSelectableNotes = this.noteHolder.notes
      .filter((nonFixedNote) => !this.isFixedNote(nonFixedNote))
      .sort((a, b) => {
        const aDistance = Math.abs(coords.y - this.getYAtNote(a));
        const bDistance = Math.abs(coords.y - this.getYAtNote(b));
        return aDistance - bDistance;
      });

    if (nearestSelectableNotes.length) {
      this.editable.editor.deselectAll(true);
      this.editable.selectedNotes = [nearestSelectableNotes[0]!];
      log(`nearest note: ${nearestSelectableNotes[0]}`);
      this.editable.selected = true;
      redrawNeeded = true;
      this.triggerListeners('select');
    }
    return redrawNeeded;
  }

  public contactEventStart(coords: Coordinates) {
    if (!this.editable.editor) {
      throw Error(
        'Editable.editor must be set before EventHandler.contactEventStart can be called.'
      );
    }

    log('contactEventStart', coords);
    this.triggerListeners('contactEventStart');

    // Add a note if it's possible to:
    // eslint-disable-next-line no-unused-vars
    const addedNote = this.addIfPossible(coords, { pend: false, select: true });

    // store these in recentEventState & recentObjectState:
    this.saveState(coords);

    // If we added a note, we are done, and need to redraw:
    if (addedNote) {
      log('addedNote', addedNote);
      if (addedNote instanceof Note) {
        this.editable.editor.deselectAll(true);
        this.editable.selectedNotes = [addedNote];
        this.editable.selected = true;
      }
      return true;
    }

    // We didn't add a note, maybe we can select one?
    return this.selectIfPossible(coords);
  }

  public drag(coords: Coordinates) {
    log('drag', coords);
    // if we don't have any selected note or pendingNote, it's possible we just dragged in
    // from another staff... let's add a note
    log('this.editable.selectedNotes.length', this.editable.selectedNotes.length);
    log('this._poppedNote', this._poppedNote);
    log('this.editable.pendingNote', this.editable.pendingNote);
    if (!this.editable.selectedNotes.length && !this._poppedNote && !this.editable.pendingNote) {
      return this.contactEventStart(coords);
    }

    // log("EventHandler Drag: " + evt);
    return this.changeNote(coords, true);
  }

  public mouseIn(coords: Coordinates) {
    log('eventHandler mouseIn', coords.x, coords.y);
    this._poppedNote = false;

    // const note = this.getNoteAtY(coords.y);

    // Be sure we're in musical bounds:
    const line = this.getStaffLineAtY(coords.y);
    if (line > this.editable.options.highestLine || line < this.editable.options.lowestLine) {
      return this.mouseOut();
    }

    // If there's not a note there already, create one and set it
    // as ghosting:
    return this.addIfPossible(coords, { pend: true, select: false });
  }

  public mouseOut() {
    log('eventHandler mouseOut');
    let redrawNeeded = false;
    // if we were ghosting, remove the ghosted note:
    if (this.editable.pendingNote) {
      this.editable.pendingNote = false;
      redrawNeeded = true;
    }

    // // if we were dragging something... remove that:
    // if (note instanceof Note) {
    //   this.noteHolder.remove(note);
    //   const noteIndex = this.editable.selectedNotes.indexOf(note);
    //   if (noteIndex !== -1) {
    //     this.editable.selectedNotes.splice(noteIndex, 1);
    //     if (this.editable.selectedNotes.length === 0) {
    //       this.editable.selected = false;
    //     }
    //   }
    //   redrawNeeded = true;
    // }

    return redrawNeeded;
  }

  public hover(coords: Coordinates): boolean | Note {
    log('hover', coords);

    if (!this.editable.clef) {
      throw Error('Editable.clef must be set before EventHandler.hover can be called.');
    }

    // if we're in rest mode, we add a hovering rest if possible:
    if (this.editable.pendingMode === 'rest') {
      return this.addRestIfPossible({ pend: true });
    }

    // If we have a popped note or a selected note, we restore it:
    if (this._poppedNote && this._poppedNote.dragMode === false) {
      return this.changeNote(coords, false);
    }

    // If there's a pending note, change it...
    if (this.editable.pendingNote) {
      return this.changeNote(coords, false);
    }

    // If we've gone out of staff-range, call a mouseOut
    const line = this.editable.clef.getLine(this.getNoteAtY(coords.y));
    if (line > this.editable.options.highestLine || line < this.editable.options.lowestLine) {
      return this.mouseOut();
    }

    // Otherwise, we add a pending note if possible:
    return this.addNoteIfPossible(coords, { pend: true, select: false });
  }

  /**
   * Changes the current selected note or poppedNote
   * @param coords
   * @param dragMode
   */
  private changeNote(coords: Coordinates, dragMode = false) {
    log('changeNote', coords, dragMode);

    // called by drag & hover -- moves selected or ghosting note.
    if (!this.editable.options.pitch) return false;

    // find the notes we're going to change:
    const noteToChange =
      this._poppedNote && this._poppedNote.dragMode === dragMode
        ? this._poppedNote.note
        : dragMode
        ? this.editable.selectedNotes[0]
        : this.editable.pendingNote;

    if (!noteToChange) {
      // this might happen if local mouseOut was called because we exceeded the max
      // leger lines -- don't treat it as an error, just politely add a note if we
      // drag back in range.
      return this.addNoteIfPossible(coords, { pend: !dragMode, select: dragMode });
    }

    // log(`changeNote ${coords.x}, ${coords.y}, drag? ${dragMode}`);

    // Compare current evt location to recentEventState location:
    const oldCoords = this.recentEventState;
    const newCoords = coords;

    const oldLine = oldCoords ? this.getStaffLineAtY(oldCoords.y) : false;
    const newLine = this.getStaffLineAtY(newCoords.y);

    log('oldLine', oldLine, 'newLine', newLine);
    if (oldLine === newLine) return false;
    this.saveState(coords);
    if (newLine > this.editable.options.highestLine || newLine < this.editable.options.lowestLine) {
      log('out of bounds note change, what shall we do?');

      if (noteToChange === this.editable.selectedNotes[0]) {
        log('delete!');
        this.triggerListeners('delete');
        return this.popNote(dragMode) || this.mouseOut();
      }
      log('mouse out!');
      return this.mouseOut();
    }

    // If we're restoring a popped note, we'll be adding it:
    if (this._poppedNote && this._poppedNote.dragMode === dragMode) {
      this.setNoteAccidental(this._poppedNote.note);
      const accidentalToApply = this._poppedNote.note.accidental;
      const restoredNote = this.addNoteIfPossible(coords, { pend: !dragMode, select: dragMode });
      if (restoredNote) {
        log(`restoring note and setting accidental ${accidentalToApply || 'natural'}`);
        restoredNote.accidental = accidentalToApply;
      }
      this.triggerListeners('pitchChange', restoredNote);
      return restoredNote;
    }

    // If we're moving a selected note, we'll be changing it or popping it:
    const noteToMove = dragMode ? this.editable.selectedNotes[0] : this.editable.pendingNote;
    if (!noteToMove) throw new Error('Cannot drag when no note is selected.');

    // If it's a fixed note, return!
    if (this.isFixedNote(noteToMove)) {
      return false;
    }

    if (!noteToMove) throw new Error('Cannot move when no note is selected or pending.');

    // If a note exists at the current location, pop the note:
    const existingNote = this.noteExistsAtY(coords.y);
    if (existingNote) {
      log('note exists! popping!!!');
      this.popNote(dragMode, true);
      this.triggerListeners('pitchChange', existingNote[0]);
      return true;
    }

    this.editable.noteHolder.remove(noteToMove);
    this.editable.pendingNote = false;
    const addedNote = this.addNoteIfPossible(coords, { pend: !dragMode, select: dragMode });
    if (addedNote) {
      addedNote.accidental = noteToMove.accidental;
      this.setNoteAccidental(addedNote);
    }
    return true;
  }

  private popNote(dragMode = false, toAvoidExistingNote = false) {
    log(`popNote: dragMode is ${dragMode}, toAvoidExistingNote is ${toAvoidExistingNote}`, this);
    const noteToMove = dragMode ? this.editable.selectedNotes[0] : this.editable.pendingNote;
    if (!noteToMove) return false;
    this._poppedNote = { note: noteToMove, dragMode };
    this.editable.pendingNote = false;
    // If we're avoiding an existing note, we've got to get rid of it, regardless:
    if (toAvoidExistingNote) {
      this.noteHolder.remove(noteToMove);
      return true;
    }
    // If we're dragging, we might want to delete it... or not, if we're not
    // allowed to delete it.
    if (dragMode) return this.delete();
    // Otherwise, it's definitely a pending note, and we've already removed
    // it from the editable, so we definitely need a redraw
    return true;
  }

  /*
   * If we're dealing with a single-note editable, the logic is easy,
   * we just set the accidental on the note, tada.
   *
   * If multiple notes, we add an accidental to any selected or ghosting
   * note -- and on touchEnd, remove this.accidental so that a new
   * ghosting note won't inherit the accidental.
   *
   */
  public setAccidental(accidental: Accidental) {
    log('set accidental @eventHandler', accidental);
    this.accidental = accidental;
    if (this.editable.selectedNotes.length !== 0) {
      this.editable.selectedNotes.forEach((note) => {
        const needRefresh = note.accidental !== accidental;
        note.accidental = accidental;
        this.accidental = '';
        if (needRefresh) this.triggerListeners('accidentalChange', note);
      });
      this.redraw();
      return;
    }

    if (this.editable.pendingNote) {
      this.editable.pendingNote.accidental = accidental;
      this.redraw();
      return;
    }

    if (!this.noteHolder.isEmpty()) {
      this.noteHolder.notes[0]!.accidental = accidental;
      this.redraw();
    }
  }

  public select() {
    log('select');

    this.editable.selected = true;
    this.triggerListeners('select');
  }

  public unselect() {
    log('unselect');

    this.editable.selected = false;
    this.editable.selectedNotes = [];
    this.triggerListeners('unselect');
  }

  public contactEventEnd() {
    log('contactEventEnd');
    const redrawNeeded =
      !!this.editable.pendingNote || (!this.editable.selected && this.editable.options.selectable);
    this._poppedNote = false;
    this.editable.pendingNote = false;
    this.accidental = '';
    return redrawNeeded;
  }

  public setRhythm(durationString: Rhythm | string) {
    if (this.noteHolder.rhythm) {
      this.noteHolder.rhythm.setDuration(durationString);
    } else {
      this.noteHolder.rhythm = new Rhythm(durationString);
    }
  }

  /**
   * Delete selected notes if deleteable.
   * Returns true if anything was deleted (and a redraw is needed)
   */
  public delete() {
    log('delete', this);
    if (!this.editable.options.deleteable || !this.editable.options.deleteable.length) return false;
    let deleted = false;

    // If we have notes we can delete...
    if (this.editable.selectedNotes.length !== 0) {
      this.editable.selectedNotes = this.editable.selectedNotes.filter((note) => {
        if (!this.isFixedNote(note)) {
          this.noteHolder.remove(note);
          deleted = true;
          return false;
        }
        return true;
      });

      // if we delete to a committed rest, keep this selected:
      if (
        this.editable.noteHolder.notes.length === 0 &&
        this.editable.options.deleteable.includes('committedRest')
      ) {
        this.editable.noteHolder.rest = true;
      } else if (
        this.editable.noteHolder.notes.length === 0 &&
        this.editable.options.deleteable.includes('placeHolderRest')
      ) {
        this.editable.placeHolderRest = true;
      }
      // if we deleted a note, but there are others to delete, let's select another note in this chord.
      if (deleted && this.editable.noteHolder.notes.length) {
        const selectable = this.noteHolder.notes.filter((note) => !this.isFixedNote(note));
        if (selectable.length) {
          this.editable.selectedNotes = selectable.slice(0, 1);
        }
      }
      if (this.editable.selectedNotes.length === 0 && !this.editable.noteHolder.rest)
        this.editable.selected = false;
      log('this.editable.selected', this.editable.selected);
      log('this.editable.selectedNotes', this.editable.selectedNotes);

      this.redraw();
      this.triggerListeners('delete');
    }

    return deleted;
  }

  public addListener(type: EditableEventTypes, callback: EventHandlerListener, thisArg?: any) {
    // adds function to be called back when status changes.
    this.listeners.push({ type, callback: thisArg ? callback.bind(thisArg) : callback });
  }

  public setParentListener(callback: EventHandlerParentListener, thisArg: any) {
    this.parentListeners = typeof thisArg !== 'undefined' ? [callback.bind(thisArg)] : [callback];
  }

  private triggerListeners(type: EditableEventTypes, ...rest: any[]) {
    this.listeners.forEach((listener) => {
      if (listener.type === type) {
        listener.callback(...rest);
      }
    }, this);
    // Send listeners to any parent listener (i.e., editor) but
    // prefix the type with "note" so "touchEventEnd" becomes
    // "noteTouchEventEnd", etc...
    this.parentListeners.forEach((listener) => {
      const parentListenerEventType = `note${type[0]!.toUpperCase()}${type.slice(
        1
      )}` as EditableParentEventTypes;
      listener(parentListenerEventType, ...rest);
    });
  }

  private redraw() {
    this.editable.redraw();
  }
}
